import React, { useState, useEffect, useRef } from 'react';
import { X, Coins } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const FLIP_DURATION_MS = 1600;

// Synthesized entirely via Web Audio (no asset files) so the toss sound is
// instant, license-free, and works offline. Requires a user-gesture-triggered
// AudioContext, which the Flip button click satisfies.
const getAudioContext = (ctxRef) => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  if (!ctxRef.current) ctxRef.current = new AudioContextClass();
  if (ctxRef.current.state === 'suspended') ctxRef.current.resume();
  return ctxRef.current;
};

// A single random metallic "tick" — the building block for the coin-machine
// rattle below. Two detuned triangle partials with a fast decay.
const scheduleCoinTick = (ctx, when, peak) => {
  const partials = [1700 + Math.random() * 1600, 2600 + Math.random() * 1800];
  partials.forEach((freq, idx) => {
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    const p = idx === 0 ? peak : peak * 0.5;
    const decay = 0.05 + Math.random() * 0.04;
    gain.gain.setValueAtTime(0, when);
    gain.gain.linearRampToValueAtTime(p, when + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.001, when + decay);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(when);
    osc.stop(when + decay + 0.02);
  });
};

// Dense, randomly-timed cascade of coin ticks for the whole time the coin is
// in the air — like a stack of coins pouring into a counting machine, rather
// than a single "whoosh" at takeoff.
const playCoinCascadeSound = (ctxRef, durationMs) => {
  const ctx = getAudioContext(ctxRef);
  if (!ctx) return;
  const start = ctx.currentTime;
  let t = 0;
  while (t < durationMs) {
    scheduleCoinTick(ctx, start + t / 1000, 0.05 + Math.random() * 0.05);
    t += 16 + Math.random() * 22; // ~25-45 ticks/sec, irregular like tumbling coins
  }
};

// Metallic double "clink" — a few short detuned sine partials with a fast
// decay, played the moment the coin lands and the result is revealed.
const playLandSound = (ctxRef) => {
  const ctx = getAudioContext(ctxRef);
  if (!ctx) return;
  const now = ctx.currentTime;
  [0, 0.09].forEach((delay, i) => {
    const partials = i === 0 ? [1800, 2650, 3600] : [1500, 2200, 3000];
    partials.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      const peak = idx === 0 ? 0.22 : 0.09;
      const start = now + delay;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(peak, start + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.35);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.4);
    });
  });
};

/**
 * Lightweight 3D coin-flip UI for the scoring header "Toss" button.
 * Purely visual for now — result is not persisted. (Distinct from
 * ../../components/common/CoinToss.jsx, which drives the cricket
 * bat/field-choice flow and needs team context this doesn't have.)
 */
const CoinFlipModal = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const [rotation, setRotation] = useState(0);
  const [flipping, setFlipping] = useState(false);
  const [result, setResult] = useState(null); // 'heads' | 'tails'
  const timeoutRef = useRef(null);
  const audioCtxRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setRotation(0);
      setFlipping(false);
      setResult(null);

      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  useEffect(() => () => {
    clearTimeout(timeoutRef.current);
    audioCtxRef.current?.close();
  }, []);

  if (!isOpen) return null;

  const handleFlip = () => {
    if (flipping) return;
    setResult(null);
    setFlipping(true);
    playCoinCascadeSound(audioCtxRef, FLIP_DURATION_MS);

    const isHeads = Math.random() < 0.5;
    const spins = 6 + Math.floor(Math.random() * 3); // 6-8 full rotations
    setRotation(prev => prev + spins * 360 + (isHeads ? 0 : 180));

    timeoutRef.current = setTimeout(() => {
      setFlipping(false);
      setResult(isHeads ? 'heads' : 'tails');
      playLandSound(audioCtxRef);
    }, FLIP_DURATION_MS);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className={`relative w-full max-w-sm ${theme.card} rounded-2xl shadow-xl overflow-hidden`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
              <Coins className="w-5 h-5 text-yellow-600" />
            </div>
            <h3 className={`text-lg font-semibold ${theme.text}`}>Coin Toss</h3>
          </div>
          <button onClick={onClose} className={`p-1 rounded-full ${theme.secondaryHover}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col items-center">
          <div
            className={`relative mb-6 ${flipping ? 'animate-coin-toss-bounce' : ''}`}
            style={{ width: 128, height: 128, perspective: 800 }}
          >
            <div
              className="w-full h-full"
              style={{
                position: 'relative',
                transformStyle: 'preserve-3d',
                transform: `rotateY(${rotation}deg)`,
                transition: flipping ? `transform ${FLIP_DURATION_MS}ms cubic-bezier(0.2, 0.6, 0.25, 1)` : 'none',
              }}
            >
              {/* Heads face */}
              <div
                className="absolute inset-0 rounded-full flex items-center justify-center text-3xl font-bold shadow-lg bg-gradient-to-br from-yellow-300 to-yellow-600 text-yellow-900 border-4 border-yellow-200"
                style={{ backfaceVisibility: 'hidden' }}
              >
                H
              </div>
              {/* Tails face */}
              <div
                className="absolute inset-0 rounded-full flex items-center justify-center text-3xl font-bold shadow-lg bg-gradient-to-br from-gray-300 to-gray-500 text-gray-800 border-4 border-gray-200"
                style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
              >
                T
              </div>
            </div>
          </div>

          <p className={`text-sm ${theme.textMuted} mb-4 h-5`}>
            {flipping
              ? 'Flipping...'
              : result
              ? `It's ${result === 'heads' ? 'Heads' : 'Tails'}!`
              : 'Tap the button to flip the coin'}
          </p>

          <button
            onClick={handleFlip}
            disabled={flipping}
            className="px-8 py-3 rounded-xl font-semibold bg-yellow-500 hover:bg-yellow-600 text-white disabled:opacity-60 transition-colors shadow-lg"
          >
            {flipping ? 'Flipping...' : result ? 'Flip Again' : 'Flip Coin'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes coinTossBounce {
          0% { transform: translateY(0); }
          40% { transform: translateY(-36px); }
          100% { transform: translateY(0); }
        }
        .animate-coin-toss-bounce {
          animation: coinTossBounce ${FLIP_DURATION_MS}ms ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default CoinFlipModal;
